import { useState } from 'react';
import { ChevronRight, Layers, LayoutGrid } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { cn } from '../../utils';
import { useEditorStore } from '../../store';
import { getToolById } from '../tools/registry';

export function LayersPanel() {
  const { design, selectedElementId, selectedRowId, selectElement } = useEditorStore();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (rowId: string) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(rowId) ? next.delete(rowId) : next.add(rowId);
      return next;
    });

  /* ── Empty state ── */
  if (design.body.rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 p-6">
        <div className="w-12 h-12 rounded-2xl bg-violet-50 dark:bg-violet-900/20
          border border-violet-200/40 dark:border-violet-700/30 flex items-center justify-center">
          <Layers className="size-6 text-violet-400 dark:text-violet-500" />
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold text-content-secondary dark:text-dark-300">No layers yet</p>
          <p className="text-xs text-content-subtle dark:text-dark-500 mt-1 leading-relaxed">
            Add rows and elements to see the layer tree here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto py-2 px-2 gap-1">
      {design.body.rows.map((row, rowIdx) => {
        const isExpanded    = expandedRows.has(row.id);
        const isRowSelected = selectedRowId === row.id && !selectedElementId;
        const totalEls      = row.columns.reduce((a, c) => a + c.elements.length, 0);

        return (
          <div key={row.id} className="rounded-xl overflow-hidden border border-line-subtle dark:border-dark-700">
            {/* Row header */}
            <button
              onClick={() => { toggleRow(row.id); selectElement(null, row.id); }}
              className={cn(
                'w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all duration-150',
                isRowSelected
                  ? 'bg-violet-600 text-white'
                  : 'bg-gray-50 dark:bg-dark-900/50 text-gray-700 dark:text-dark-300 hover:bg-gray-100 dark:hover:bg-dark-700'
              )}
            >
              {/* Expand chevron */}
              <ChevronRight className={cn(
                'size-3.5 shrink-0 transition-transform duration-200',
                isExpanded && 'rotate-90',
                isRowSelected ? 'text-white/80' : 'text-content-subtle dark:text-dark-500'
              )} />

              {/* Row icon */}
              <div className={cn(
                'w-6 h-6 rounded-lg flex items-center justify-center shrink-0',
                isRowSelected
                  ? 'bg-white/20'
                  : 'bg-violet-100 dark:bg-violet-900/30'
              )}>
                <LayoutGrid className={cn('size-3', isRowSelected ? 'text-white' : 'text-violet-500 dark:text-violet-400')} />
              </div>

              <div className="flex-1 min-w-0">
                <p className={cn(
                  'text-xs font-bold truncate',
                  isRowSelected ? 'text-white' : 'text-gray-700 dark:text-dark-200'
                )}>
                  Row {rowIdx + 1}
                </p>
                <p className={cn(
                  'text-[10px]',
                  isRowSelected ? 'text-white/70' : 'text-content-subtle dark:text-dark-500'
                )}>
                  {row.columns.length} col{row.columns.length !== 1 ? 's' : ''} · {totalEls} element{totalEls !== 1 ? 's' : ''}
                </p>
              </div>
            </button>

            {/* Expanded columns + elements */}
            {isExpanded && (
              <div className="bg-surface dark:bg-dark-800 border-t border-line-subtle dark:border-dark-700">
                {row.columns.map((col, colIdx) => (
                  <div key={col.id}>
                    {/* Column label */}
                    <div className="flex items-center gap-2 px-4 py-1.5 bg-gray-50/60 dark:bg-dark-900/40
                      border-b border-line-subtle dark:border-dark-700/60">
                      <div className="w-1 h-4 rounded-full bg-violet-300 dark:bg-violet-600/50 opacity-60" />
                      <span className="text-[10px] font-bold uppercase tracking-widest text-content-subtle dark:text-dark-500">
                        Column {colIdx + 1}
                      </span>
                      <span className="ml-auto text-[10px] text-gray-300 dark:text-dark-600">
                        {col.width}%
                      </span>
                    </div>

                    {/* Elements */}
                    {col.elements.length === 0 ? (
                      <div className="px-5 py-2 text-[10px] text-gray-300 dark:text-dark-600 italic">
                        Empty column
                      </div>
                    ) : (
                      col.elements.map((el) => {
                        const tool      = getToolById(el.type);
                        const Icon      = tool
                          ? (LucideIcons as unknown as Record<string, LucideIcon>)[tool.icon]
                          : null;
                        const isSelected = selectedElementId === el.id;

                        return (
                          <button
                            key={el.id}
                            onClick={() => selectElement(el.id, row.id)}
                            className={cn(
                              'w-full flex items-center gap-2.5 pl-5 pr-3 py-2 text-left transition-all duration-150',
                              'border-b border-gray-50 dark:border-dark-700/40 last:border-0',
                              isSelected
                                ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300'
                                : 'text-content-secondary dark:text-dark-400 hover:bg-gray-50 dark:hover:bg-dark-700'
                            )}
                          >
                            {/* Element type icon */}
                            <div className={cn(
                              'w-6 h-6 rounded-lg flex items-center justify-center shrink-0 transition-all',
                              isSelected
                                ? 'bg-violet-100 dark:bg-violet-900/40'
                                : 'bg-gray-100 dark:bg-dark-700'
                            )}>
                              {Icon
                                ? <Icon className={cn('size-3', isSelected ? 'text-violet-500' : 'text-content-subtle dark:text-dark-500')} />
                                : <Layers className="size-3 text-gray-300" />}
                            </div>

                            <span className={cn(
                              'flex-1 text-xs font-medium truncate',
                              isSelected && 'font-semibold'
                            )}>
                              {tool?.label ?? el.type}
                            </span>

                            {isSelected && (
                              <div className="w-1.5 h-1.5 rounded-full bg-violet-500 shrink-0" />
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
